import { useFeatureFlagsContext } from 'src/providers/FeatureFlagsProvider/FeatureFlags.context';

/**
 * Single gate for every WAPI wallet surface (RET-1763).
 *
 * Lives here rather than as another derived boolean on FeatureFlagsContext on
 * purpose: that context is consumed app-wide, and every value added to its
 * `useMemo` widens the re-render blast radius. The wallet reads the raw flag
 * map and derives its own answer, so nothing outside this slice re-renders
 * when the wallet ships.
 *
 * Two different questions, two different keys — pick by what the caller is
 * deciding:
 *
 * - `isLoadingWalletFlag` (= `isLoadingFeatureFlags`) tracks a fetch being in
 *   flight, and is what this subsystem already keys its own behaviour off of
 *   (send gates, the wallet provider, WhatsappApi.hook's deep-link handling —
 *   see those files). What it cannot tell you is whether the map currently on
 *   screen belongs to the CURRENT workspace. The provider raises it from
 *   inside the fetch effect, so there is one render BEFORE that effect runs
 *   where it reads `false` over a map that is not this workspace's: the
 *   defaults on a cold load, or the previous workspace's map right after a
 *   switch. The provider documents that render itself (`:307-311`: "the first
 *   render after a direct page load shows loading=false with defaults, one
 *   render before the fetch effect flips it"). A visibility guard built on it
 *   paints the wrong cohort for that render. Existing consumers accept that
 *   trade-off already; changing it for them is out of scope here.
 * - `hasLoadedWalletFlag` (= `hasLoadedFeatureFlags`) is true only once THIS
 *   workspace's flags fetch has settled, success or failure
 *   (`FeatureFlags.types.ts:20-23`: "Route guards gate on this, never on
 *   isLoadingFeatureFlags alone"; `FeatureFlags.provider.tsx:312`,
 *   `settledWorkspaceId === currentWorkspaceId`). Use this for any
 *   VISIBILITY decision — showing or hiding a whole surface based on the
 *   cohort — because it is the only one of the two that is actually keyed by
 *   workspace. `Inbox.page.tsx:51-58` gates its route the same way, for the
 *   same reason: `isLoadingFeatureFlags` reads `false` with defaults still on
 *   screen for one render before the fetch effect flips it, and a visibility
 *   guard built on it flashes the wrong cohort in that render.
 *
 * Additive: both keys existed before this comment; `hasLoadedWalletFlag` is
 * new. `isWalletEnabled` and `isLoadingWalletFlag` are unchanged, and no
 * existing consumer of this hook is touched by adding it.
 */
export function useWalletFlag() {
  const { featureFlags, isLoadingFeatureFlags, hasLoadedFeatureFlags } =
    useFeatureFlagsContext();

  return {
    isWalletEnabled: featureFlags['billing_wapi-wallet'],
    isLoadingWalletFlag: isLoadingFeatureFlags,
    hasLoadedWalletFlag: hasLoadedFeatureFlags,
  };
}
